/**
 * Configuration and quality presets. ARCHITECTURE.md §9.
 *
 * Persisted to localStorage under `coi.settings.v1`. Unknown keys are dropped on load so a
 * stale save can never inject an invalid preset. Three-free and safe in Node (persistence
 * silently no-ops without a DOM).
 */

const STORAGE_KEY = 'coi.settings.v1';

export const QUALITY_PRESETS = Object.freeze({
  low: Object.freeze({
    renderScale: 0.65, shadowMapSize: 1024, shadowCascades: 1, ssao: false,
    taa: false, fxaa: true, bloom: 'cheap', motionBlur: false, volumetrics: false,
    rainDensity: 0.25, particleBudget: 400, decalBudget: 32, reflections: 'none',
    crowdDensity: 0.25, propDetail: 'low', contactShadows: false, anisotropy: 1,
  }),
  medium: Object.freeze({
    renderScale: 0.85, shadowMapSize: 1536, shadowCascades: 2, ssao: true,
    taa: false, fxaa: true, bloom: 'standard', motionBlur: false, volumetrics: false,
    rainDensity: 0.55, particleBudget: 900, decalBudget: 64, reflections: 'planar-half',
    crowdDensity: 0.55, propDetail: 'medium', contactShadows: true, anisotropy: 4,
  }),
  high: Object.freeze({
    renderScale: 1.0, shadowMapSize: 2048, shadowCascades: 3, ssao: true,
    taa: true, fxaa: false, bloom: 'standard', motionBlur: true, volumetrics: true,
    rainDensity: 1.0, particleBudget: 1600, decalBudget: 96, reflections: 'planar',
    crowdDensity: 1.0, propDetail: 'high', contactShadows: true, anisotropy: 8,
  }),
  ultra: Object.freeze({
    renderScale: 1.0, shadowMapSize: 3072, shadowCascades: 4, ssao: true,
    taa: true, fxaa: false, bloom: 'wide', motionBlur: true, volumetrics: true,
    rainDensity: 1.35, particleBudget: 2600, decalBudget: 128, reflections: 'planar',
    crowdDensity: 1.4, propDetail: 'high', contactShadows: true, anisotropy: 16,
  }),
});

export const PRESET_NAMES = Object.freeze(['low', 'medium', 'high', 'ultra']);

/** Every persisted setting with its default. Load drops anything not listed here. */
const DEFAULTS = Object.freeze({
  // graphics
  preset: 'high',
  dynamicResolution: true,
  vsync: true,
  maxFps: 0, // 0 = uncapped
  motionBlurStrength: 0.45,
  cameraShakeScale: 1.0,
  colorGrade: 'iron', // 'iron' | 'neutral' | 'noir'
  exposureBias: 0.0,

  // audio, all in [0,1]
  audioMaster: 0.8,
  audioSfx: 0.9,
  audioMusic: 0.55,
  audioAmbience: 0.6,
  audioVoice: 0.9,
  audioDucking: true,

  // gameplay
  roundSeconds: 60,
  bestOf: 3,
  aiDifficulty: 'skilled', // 'rookie' | 'club' | 'skilled' | 'veteran' | 'iron'
  inputBufferF60: 10,
  showFrameData: false,
  showHitboxes: false,
  showInputHistory: false,
  showDamageNumbers: true,

  // accessibility / readability
  highContrastFighters: false,
  reduceRain: false,
  reduceFlashing: false,
  uiScale: 1.0,
});

export class ConfigStore {
  constructor(overrides = {}) {
    this._values = { ...DEFAULTS };
    this._listeners = new Map();
    this.load();
    // Explicit overrides (tools, deterministic captures) win over persisted values
    // and are never written back to storage.
    for (const k in overrides) {
      if (k in DEFAULTS) this._values[k] = overrides[k];
    }
    this._overrideKeys = new Set(Object.keys(overrides).filter((k) => k in DEFAULTS));
  }

  get(key) {
    return this._values[key];
  }

  /** Sets a value, notifies listeners, and persists (unless the key is an override). */
  set(key, value) {
    if (!(key in DEFAULTS)) throw new Error(`Unknown setting "${key}"`);
    const prev = this._values[key];
    if (prev === value) return value;
    this._values[key] = value;
    this._notify(key, value, prev);
    this.save();
    return value;
  }

  /** Returns the resolved quality preset object for the current `preset` setting. */
  get quality() {
    return QUALITY_PRESETS[this._values.preset] || QUALITY_PRESETS.high;
  }

  get presetName() {
    return this._values.preset;
  }

  /** Audio sub-object in the shape ARCHITECTURE.md §9 documents. */
  get audio() {
    const v = this._values;
    return {
      master: v.audioMaster,
      sfx: v.audioSfx,
      music: v.audioMusic,
      ambience: v.audioAmbience,
      voice: v.audioVoice,
      ducking: v.audioDucking,
    };
  }

  /**
   * Subscribes to a single key, or '*' for any change.
   * @returns {() => void} unsubscribe
   */
  onChange(key, fn) {
    let arr = this._listeners.get(key);
    if (!arr) {
      arr = [];
      this._listeners.set(key, arr);
    }
    arr.push(fn);
    return () => {
      const i = arr.indexOf(fn);
      if (i >= 0) arr.splice(i, 1);
    };
  }

  _notify(key, value, prev) {
    const direct = this._listeners.get(key);
    if (direct) for (const fn of direct) fn(value, prev, key);
    const wild = this._listeners.get('*');
    if (wild) for (const fn of wild) fn(value, prev, key);
  }

  snapshot() {
    return { ...this._values };
  }

  /** Applies many values at once with a single notification burst. */
  patch(obj) {
    for (const k in obj) if (k in DEFAULTS) this.set(k, obj[k]);
  }

  resetToDefaults() {
    const prev = this._values;
    this._values = { ...DEFAULTS };
    for (const k in DEFAULTS) if (prev[k] !== this._values[k]) this._notify(k, this._values[k], prev[k]);
    this.save();
  }

  load() {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      for (const k in parsed) {
        if (!(k in DEFAULTS)) continue; // drop unknown keys
        const expected = typeof DEFAULTS[k];
        if (typeof parsed[k] !== expected) continue; // drop wrong types
        if (k === 'preset' && !PRESET_NAMES.includes(parsed[k])) continue;
        this._values[k] = parsed[k];
      }
    } catch {
      // Corrupt save: fall back to defaults rather than failing to boot.
    }
  }

  save() {
    if (typeof localStorage === 'undefined') return;
    try {
      const out = {};
      for (const k in this._values) {
        if (this._overrideKeys && this._overrideKeys.has(k)) continue;
        out[k] = this._values[k];
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(out));
    } catch {
      // Storage disabled / full: settings simply do not persist this session.
    }
  }
}

export { DEFAULTS as CONFIG_DEFAULTS };
